import { Injectable } from '@angular/core';
import * as moment from 'moment-timezone';

import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';

import { API } from '@app-core/constants/api.constants';
import {
  ARCGIS_DOMAIN,
  AWS_DOMAIN,
  DEFAULT_TIMEZONE,
  PORTAL_RELEASE_VERSION,
  TRANSLATION_URL_PATTERN,
} from '@app-core/constants/constants';
import { AccessService } from '@app-core/services/access/access.service';
import { DataService } from '@app-core/services/data/data.service';
import { environment } from '@env/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthenticationService } from '@app-auth/services/authentication.service';

// Keep track of translation URLs to prevent adding AUTH tokens to them
// This is a workaround for the issue where the interceptor is called multiple times for the same request
// and the translation URLs are not being handled correctly.
// Keeping it outside the class to avoid re-initialization on each request
// and to ensure that the URLs are shared across all instances of the interceptor
const translationURLs = new Set();

@Injectable()
export class HttpInterceptorService implements HttpInterceptor {
  private intermediateServerHost = new URL(environment.intermediate_server_url).host;

  public currentFleet: string;
  public timezone: string;

  constructor(private accessService: AccessService, private dataService: DataService, private authService: AuthenticationService) {}

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // If the request URL is already in the translationURLs set
    // This is to prevent adding AUTH tokens to translation files
    if (translationURLs.has(req.url)) {
      return next.handle(req);
    }

    if (req.url.includes('assets/i18n/')) {
      const lang = req.url.split('/').pop().split('.').reverse().pop(); // Extract the language file name

      const translationURL = this.getTranslationURL(lang);
      translationURLs.add(translationURL);

      const modifiedReq = req.clone({ url: translationURL });
      return next.handle(modifiedReq);
    }

    let postReq;
    /**
     * Get Token from localstorage
     */
    const loginInfo = this.accessService.getLoginInfo();
    this.dataService._currentFleet.subscribe((value: string) => {
      if (value) {
        this.currentFleet = value;
      }
    });
    this.dataService._currentTimeZone.subscribe((value: string) => {
      if (value) {
        if (value !== DEFAULT_TIMEZONE) {
          this.timezone = value;
        } else {
          this.timezone = moment.tz.guess();
        }
      }
    });

    /**
     * Clone the request object and set the headers
     */
    if (loginInfo && loginInfo.token) {
      let host: string;
      // new URL will throw error if relative url is passed
      if (![API.GEO_JSON, ...API.TRANSLATE_JSON].includes(req.url)) {
        host = new URL(req.url).host;
      }

      if (req.url.includes(ARCGIS_DOMAIN) || req.url.includes(AWS_DOMAIN)) {
        postReq = req.clone({
          withCredentials: host === this.intermediateServerHost,
        });
      } else {
        postReq = req.clone({
          // Set token
          headers: req.headers.set('x-access-token', loginInfo.token).set('x-user-timezone', this.timezone),
          withCredentials: host === this.intermediateServerHost,
          setParams:
            req.url.includes(API.EULA_CONSENT) && req.params.has('fleetId')
              ? {}
              : {
                  fleetId: this.currentFleet,
                },
        });
      }
    } else {
      postReq = req.clone();
    }

    return next.handle(postReq).pipe(
      map(this.handleResponse.bind(this)),
      catchError((err: any): Observable<any> => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === 0) {
            // show no internet when status is 0
            return throwError('No internet');
          } else if (err.status === 401) {
            // Logout when the token expires
            this.logOut();
            return throwError(err.error || 'Unauthorized');
          }
        }
        return throwError(err);
      })
    );
  }

  /**.
   * Handle response
   */
  private handleResponse(event: HttpEvent<any>): HttpEvent<any> {
    return event;
  }

  /**
   * Handle error response
   * Check for status zero and throw no internet error
   * else forward the error
   */
  // private handleError(err: any): Observable<any> {
  //    if (err instanceof HttpErrorResponse) {
  //       if (err.status === 0) {
  //          // show no internet when status is 0
  //          return throwError('No internet');
  //       } else if (err.status === 401) {
  //          // Logout when the token expires
  //          console.log('came in', err);
  //          this.logOut();
  //          return throwError(err.error || 'Unauthorized');
  //       }
  //    }
  //    return throwError(err);
  // }

  private logOut() {
    this.authService.logout();
  }

  private getTranslationURL(lang: string) {
    return (environment.translation_url_pattern || TRANSLATION_URL_PATTERN)
      .replace('{portal}', 'fleet')
      .replace('{version}', environment.translation_version || PORTAL_RELEASE_VERSION)
      .replace('{env}', environment.translation_env || environment.env)
      .replace('{lang}', lang)
      .replace('{clientId}', this.currentFleet);
  }
}
